export const offerSetValidationConditions = {
  new: 'N',
  used: 'U'
} as const;

export type OfferSetValidationCondition =
  typeof offerSetValidationConditions[keyof typeof offerSetValidationConditions] | null;

export const offerSetValidationDataFields = {
  msrp: 'msrp',
  buyPrice: 'buyPrice',
  leasePayment: 'leasePayment',
  financePayment: 'financePayment',
  savings: 'savings',
  savingsPercentOfMsrp: 'savingsPercentOfMsrp',
  unrebatedDiscountPercentOfMsrp: 'unrebatedDiscountPercentOfMsrp',
  rebatedDiscountPercentOfMsrp: 'rebatedDiscountPercentOfMsrp',
  finalBuyPricePercentOfMsrp: 'finalBuyPricePercentOfMsrp'
} as const;

export type OfferSetValidationDataField =
  typeof offerSetValidationDataFields[keyof typeof offerSetValidationDataFields];

export const offerSetValidationOperators = {
  isEmpty: 'is empty',
  greaterThan: 'greater than',
  lessThan: 'less than',
  greaterThanOrEqual: 'greater than or equal to',
  lessThanOrEqual: 'less than or equal to',
  equal: 'equal to',
  notEqual: 'not equal to'
} as const;

export type OfferSetValidationOperator =
  typeof offerSetValidationOperators[keyof typeof offerSetValidationOperators];

export const offerSetValidationValueFormats = {
  currency: 'currency',
  percent: 'percent'
} as const;

export type OfferSetValidationValueFormat =
  typeof offerSetValidationValueFormats[keyof typeof offerSetValidationValueFormats];

export interface OfferSetValidationDataFieldConfiguration {
  label: string;
  valueFormat: OfferSetValidationValueFormat;
  operators: readonly OfferSetValidationOperator[];
}

const allowedOperators = Object.values(offerSetValidationOperators);

export const offerSetValidationDataFieldConfigurations:
  Readonly<Record<OfferSetValidationDataField, OfferSetValidationDataFieldConfiguration>> = {
  [offerSetValidationDataFields.msrp]: {
    label: 'MSRP',
    valueFormat: offerSetValidationValueFormats.currency,
    operators: allowedOperators
  },
  [offerSetValidationDataFields.buyPrice]: {
    label: 'Buy Price',
    valueFormat: offerSetValidationValueFormats.currency,
    operators: allowedOperators
  },
  [offerSetValidationDataFields.leasePayment]: {
    label: 'Lease Payment',
    valueFormat: offerSetValidationValueFormats.currency,
    operators: allowedOperators
  },
  [offerSetValidationDataFields.financePayment]: {
    label: 'Finance Payment',
    valueFormat: offerSetValidationValueFormats.currency,
    operators: allowedOperators
  },
  [offerSetValidationDataFields.savings]: {
    label: 'Savings',
    valueFormat: offerSetValidationValueFormats.currency,
    operators: allowedOperators
  },
  [offerSetValidationDataFields.savingsPercentOfMsrp]: {
    label: 'Savings Percent of MSRP',
    valueFormat: offerSetValidationValueFormats.percent,
    operators: allowedOperators
  },
  [offerSetValidationDataFields.unrebatedDiscountPercentOfMsrp]: {
    label: 'Unrebated Discount Percent of MSRP',
    valueFormat: offerSetValidationValueFormats.percent,
    operators: allowedOperators
  },
  [offerSetValidationDataFields.rebatedDiscountPercentOfMsrp]: {
    label: 'Rebated Discount Percent of MSRP',
    valueFormat: offerSetValidationValueFormats.percent,
    operators: allowedOperators
  },
  [offerSetValidationDataFields.finalBuyPricePercentOfMsrp]: {
    label: 'Final Buy Price Percent of MSRP',
    valueFormat: offerSetValidationValueFormats.percent,
    operators: allowedOperators
  }
};

export interface OfferSetValidationRulePayload {
  vehicleCondition: OfferSetValidationCondition;
  dataField: OfferSetValidationDataField;
  operator: OfferSetValidationOperator;
  value: number;
  enabled: boolean;
}

export interface OfferSetValidationRuleModel extends OfferSetValidationRulePayload {
  readonly id: number;
  readonly created: string;
  readonly modified: string | null;
}
